// ============================================================
// Tiny Patio — newsletter signup without leaving the page
//
// Every newsletter box in the theme (footer, mobile footer, CTA band,
// blog sidebar, article foot, and the Monthly Kit Draw popup) is the
// NATIVE {% form 'customer' %}. That is deliberate: only native theme
// forms fire Shopify's double opt-in email and Flow's "Customer
// subscribed to email marketing" trigger, so the form tag stays.
//
// What we take away is the page RELOAD. Left alone the native form
// POSTs to /contact#contact_form and comes back as a fresh page load,
// which caused two bugs at once:
//   1. the browser jumps to the first id="contact_form" on the page —
//      the footer — so subscribing from the popup threw the visitor
//      down to the bottom of the page;
//   2. form.posted_successfully? is true for EVERY customer form on
//      that render, so the footer printed "Check your inbox" inside its
//      own input row (a flex row, so the note landed beside Subscribe).
//
// Posting with fetch instead is byte-identical to the server — same
// endpoint, same form_type, same fields, so the opt-in email and Flow
// are unaffected — but the scroll position and the footer stay exactly
// as they were, and every confirmation is handed to one place: the
// popup's "Check your inbox" card.
// ============================================================
(function () {
  'use strict';

  if (!window.fetch || !window.FormData) return;   // leave the native POST alone

  var STRINGS = window.TP_STRINGS || {};
  var THANKS = STRINGS.newsletter_thanks || 'Check your inbox — please confirm your email to finish.';
  var ERROR = STRINGS.newsletter_error || 'That email didn’t look right — try again?';

  // {% form 'customer' %} is the newsletter signup. The contact page's
  // {% form 'contact' %} posts to the same URL, hence keying off form_type
  // rather than the action.
  function isNewsletterForm(form) {
    var type = form.querySelector('input[name="form_type"]');
    return !!type && type.value === 'customer';
  }

  // Registered by sections/newsletter-popup.liquid. Absent only when the
  // merchant has switched the popup off altogether.
  function popupApi() {
    var api = window.TPNewsletterPopup;
    return api && typeof api.success === 'function' ? api : null;
  }

  // Leaves the DOM byte-for-byte as it found it — the footer form must look
  // untouched afterwards, empty style="" attributes included.
  function setBusy(form, busy) {
    var button = form.querySelector('[type="submit"]');
    if (busy) form.setAttribute('aria-busy', 'true');
    else form.removeAttribute('aria-busy');
    if (!button) return;
    button.disabled = busy;                 // no label swap: it would resize the button
    if (busy) {
      button.style.opacity = '0.6';
    } else if (button.style.opacity) {
      button.style.removeProperty('opacity');
      if (!button.getAttribute('style')) button.removeAttribute('style');
    }
  }

  // Said next to the form when there is no popup to say it in. Deliberately a
  // SIBLING of <form>, never a child: the footer form is a flex row, so a note
  // inside it becomes a third column squeezed against the button — the exact
  // layout the server-rendered note used to break.
  function inlineNote(form, message, isError) {
    var note = form.nextElementSibling;
    if (!note || !note.hasAttribute('data-nl-note')) {
      note = document.createElement('p');
      note.setAttribute('data-nl-note', '');
      note.setAttribute('role', 'status');
      note.style.cssText = 'margin:10px 0 0;font-size:13px;line-height:1.55;';
      form.insertAdjacentElement('afterend', note);
    }
    note.style.color = isError ? '#B3261E' : '';
    note.style.opacity = isError ? '1' : '0.9';
    note.textContent = message;
  }

  function succeed(form) {
    var api = popupApi();
    form.reset();
    var slot = form.querySelector('[data-nl-error]');
    if (slot) slot.hidden = true;
    if (api) {
      api.success();
      return;
    }
    inlineNote(form, THANKS, false);
  }

  function fail(form, message) {
    // The popup carries a styled slot of its own; everything else gets a note
    // under the form.
    var slot = form.querySelector('[data-nl-error]');
    if (slot) {
      slot.textContent = message;
      slot.hidden = false;
      return;
    }
    inlineNote(form, message, true);
  }

  // A failed POST re-renders the page with form.errors filled in. Lift
  // Shopify's own wording out of it when we can — it is more use than
  // "something went wrong".
  function errorFrom(html) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector('[data-nl-error]:not([hidden]), .tp-pop__err, .errors li, .errors');
      return el ? el.textContent.trim() : '';
    } catch (e) {
      return '';
    }
  }

  function send(form) {
    setBusy(form, true);

    fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'text/html, */*' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        // Spam protection kicked in: Shopify wants a captcha solved on its own
        // page, which only a real navigation can do.
        if (/\/challenge/i.test(res.url)) return { native: true };
        // Shopify redirects to <this page>?customer_posted=true on success and
        // fetch follows it, so the final URL is the answer.
        if (/customer_posted=true/i.test(res.url)) return { ok: true };
        // Rate limited (429/430) or a server fault: not the visitor's mistake,
        // and not worth losing the signup over — hand it back to the browser.
        if (res.status === 429 || res.status === 430 || res.status >= 500) return { native: true };
        return res.text().then(function (html) {
          return { message: errorFrom(html) };
        });
      })
      .then(function (result) {
        if (result.native) {
          form.submit();                    // full POST; .submit() skips this handler
          return;
        }
        setBusy(form, false);
        if (result.ok) succeed(form);
        else fail(form, result.message || ERROR);
      })
      .catch(function () {
        form.submit();
      });
  }

  // Delegated, so forms re-rendered by the Section Rendering API or the Theme
  // Customiser are covered without re-binding.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!isNewsletterForm(form)) return;
    if (form.getAttribute('aria-busy') === 'true') {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    send(form);
  });
})();
